/*
 * HARUM (HAR-208 + HAR-209): behaviour for the customer dashboard at
 * /pages/dashboard.
 *
 * Five independent pieces, each safe to fail on its own:
 *   1. Panel switching (with the panel name in the URL hash, so a panel can be
 *      linked to and survives the order pagination reloading the page).
 *   2. Custom orders (HAR-209): fetch from the Shopify App Proxy that fronts
 *      supply.harum.io, group into the six tabs, render.
 *   3. Wishlist: read the browser-stored list written by harum-wishlist.js.
 *   4. Addresses: submit and delete in the background so Shopify's redirect to
 *      /account/addresses does not throw the customer off the dashboard.
 *   5. "Chat With Us": open the Shopify Inbox widget, or follow a fallback link.
 *
 * Country/province dropdowns and the edit-form toggles are the theme's own
 * customer.js - not re-implemented here.
 */
(function () {
  /* ------------------------------------------------------------------ *
   * 1. Panels
   * ------------------------------------------------------------------ */

  function setupPanels(root) {
    const tabs = Array.from(root.querySelectorAll('[data-harum-dash-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-harum-dash-panel]'));
    if (!tabs.length || !panels.length) return;

    const names = panels.map((panel) => panel.dataset.harumDashPanel);

    function show(name, updateHash) {
      if (names.indexOf(name) === -1) name = names[0];

      panels.forEach((panel) => {
        panel.hidden = panel.dataset.harumDashPanel !== name;
      });
      tabs.forEach((tab) => {
        tab.setAttribute('aria-selected', tab.dataset.harumDashTab === name ? 'true' : 'false');
      });

      if (updateHash) {
        /* replaceState keeps the back button meaning "the page before the
           dashboard" rather than "the previous tab". */
        window.history.replaceState(null, '', `#${name}`);
      }
    }

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => show(tab.dataset.harumDashTab, true));
    });

    root.addEventListener('click', (event) => {
      const jump = event.target.closest('[data-harum-dash-goto]');
      if (!jump) return;
      show(jump.dataset.harumDashGoto, true);
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    window.addEventListener('hashchange', () => show(window.location.hash.replace('#', ''), false));

    show(window.location.hash.replace('#', ''), false);
  }

  /* ------------------------------------------------------------------ *
   * 2. Custom orders (HAR-209)
   * ------------------------------------------------------------------ */

  /*
   * The stages HAR-209 signed off, plus `processed` for the pre-2026-05 history,
   * and the six tabs they roll up into. This table is the single source of
   * truth here: tab counts, filtering and badge colours all read from it, so
   * they cannot drift apart.
   *
   * The stage itself is decided by supply.harum.io, which derives it from the
   * linked Shopify order - see lib/customOrders/customerStage.ts there. Keep the
   * keys below in step with CUSTOM_ORDER_STAGES in that file.
   */
  const CUSTOM_ORDER_STATUSES = {
    requested: { label: 'Requested', tint: 'neutral', group: 'requested' },
    pending_payment: { label: 'Pending Payment', tint: 'warning', group: 'requested' },
    /* Requests from the pre-2026-05 flow, quoted and closed by email with no
       draft order and so nothing left to pay or track. Grouped with the other
       finished requests rather than shown as owing money. */
    processed: { label: 'Processed', tint: 'neutral', group: 'delivered' },
    paid: { label: 'Paid', tint: 'info', group: 'processing' },
    purchased: { label: 'Purchased', tint: 'info', group: 'processing' },
    /* `holdable` (HAR-12): the parcel has not left us yet, so the customer can
       still choose Ship Now or Ship Later. Once it is out for delivery it is
       too late to hold it. */
    to_warehouse: { label: 'To Warehouse', tint: 'progress', group: 'shipping', holdable: true },
    reach_warehouse: { label: 'Reached Warehouse', tint: 'progress', group: 'shipping', holdable: true },
    awaiting_courier: { label: 'Awaiting Courier', tint: 'progress', group: 'shipping', holdable: true },
    out_for_delivery: { label: 'Out for Delivery', tint: 'progress', group: 'shipping' },
    delivered: { label: 'Delivered', tint: 'success', group: 'delivered' },
    canceled: { label: 'Cancelled', tint: 'danger', group: 'closed' },
    refunded: { label: 'Refunded', tint: 'danger', group: 'closed' },
  };

  /* Spelling variants only - the backend is the authority on which stage a
     request is in, this just keeps a British "cancelled" or a longer
     "reached_warehouse" from falling through to the unknown badge. */
  const STATUS_ALIASES = {
    cancelled: 'canceled',
    reached_warehouse: 'reach_warehouse',
  };

  const UNKNOWN_STATUS = { label: 'In progress', tint: 'neutral', group: 'processing' };

  function normaliseStatus(value) {
    const key = String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_');
    if (CUSTOM_ORDER_STATUSES[key]) return Object.assign({ key }, CUSTOM_ORDER_STATUSES[key]);
    const alias = STATUS_ALIASES[key];
    if (alias) return Object.assign({ key: alias }, CUSTOM_ORDER_STATUSES[alias]);
    return Object.assign({ key: key || 'unknown' }, UNKNOWN_STATUS);
  }

  /* One request per status so a design review can see every badge. Reachable
     with ?harum_custom_orders_demo=1 or the section's sample-data setting. */
  function demoRequests() {
    return Object.keys(CUSTOM_ORDER_STATUSES).map((status, index) => ({
      id: `demo-${index + 1}`,
      reference: `CO-2026-${String(index + 1).padStart(4, '0')}`,
      created_at: `2026-0${(index % 8) + 1}-1${index % 9}`,
      status,
      tracking_number: index > 5 ? `HRM${String(index).padStart(9, '0')}KR` : '',
      tracking_url: '',
      tracking_company: index > 5 ? 'FedEx' : '',
      invoice_url: status === 'pending_payment' ? '#demo-invoice' : '',
      /* HAR-12: one request already on a paid hold, so the "holding until"
         state can be reviewed next to the Ship Now / Ship Later ones. */
      hold_until: status === 'awaiting_courier' ? '2026-12-15' : '',
      total: 42.5 + index * 7.25,
      currency: 'USD',
      items: [
        {
          title: 'Sample item from a Korean marketplace',
          url: '',
          price: 21.25 + index,
          quantity: 1,
        },
        {
          title: 'Second sample item',
          url: '',
          price: 21.25,
          quantity: index % 3 === 0 ? 2 : 1,
        },
      ].slice(0, (index % 2) + 1),
    }));
  }

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null && text !== '') node.textContent = text;
    return node;
  }

  /* Locales are pinned to en-GB / en-US rather than the browser's, so a card
     rendered by JS reads the same as the Liquid-rendered order cards next to it
     ("10 Jan 2026", "$42.50") on a browser set to any language. */
  function formatDate(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  function formatAmount(value, currency) {
    if (value === undefined || value === null || value === '') return '';
    if (typeof value === 'string' && /[^\d.,\s]/.test(value)) return value; // already formatted
    const amount = Number(String(value).replace(/,/g, ''));
    if (Number.isNaN(amount)) return String(value);
    try {
      return amount.toLocaleString('en-US', {
        style: 'currency',
        currency: currency || 'USD',
        currencyDisplay: 'narrowSymbol',
      });
    } catch (error) {
      return `${currency || ''} ${amount.toFixed(2)}`.trim();
    }
  }

  /*
   * HAR-12: Ship Now / Ship Later on a custom order that is still with us.
   *
   * Every order is held free for 30 days and ships on its own after that. A
   * customer who wants it held longer buys a holding-period product (one
   * variant per block of days, priced by Marketing), up to 60 days in total.
   *
   * - Ship Later reveals those variants on the card and adds the chosen one to
   *   the cart with the request reference as a line item property, so the paid
   *   order says which request it extends.
   * - Ship Now asks supply.harum.io to release the parcel. It posts to the
   *   section's ship-now endpoint, which must be an App Proxy path for the same
   *   reason as the list endpoint: Shopify signs the request and supplies the
   *   customer id, so the backend can check the request is theirs.
   *
   * `hold_until` on a request (set by the backend once a hold is paid) switches
   * the card to "held until <date>" with only Ship Now left to press.
   */
  const SHIP_CHOICE_DEMO_OPTIONS = [
    { id: 'demo-15', title: 'Hold 15 more days', price: '$5.00' },
    { id: 'demo-30', title: 'Hold 30 more days', price: '$9.00' },
  ];

  function renderShipChoice(request, shipChoice) {
    const holdUntil = request.hold_until ? formatDate(request.hold_until) : '';
    const canShipNow = Boolean(shipChoice.endpoint) || shipChoice.demo;
    const canShipLater = !holdUntil && shipChoice.options.length > 0;
    if (!canShipNow && !canShipLater) return null;

    const wrap = element('div', 'harum-dash__ship-choice');
    const note = element(
      'p',
      'harum-dash__ship-note',
      holdUntil
        ? `We are holding this order until ${holdUntil}. It ships automatically on that date, or sooner if you ask.`
        : 'Not ready for it yet? We hold every order free for 30 days, and up to 60 days in total.'
    );
    wrap.appendChild(note);

    const actions = element('div', 'harum-dash__ship-actions');
    const message = element('p', 'harum-dash__ship-message');
    message.setAttribute('role', 'status');
    message.hidden = true;
    let panel = null;

    function say(text) {
      message.textContent = text;
      message.hidden = false;
    }

    if (canShipNow) {
      const now = element('button', 'btn btn--primary btn--small');
      now.type = 'button';
      now.appendChild(element('span', 'btn__text', 'Ship Now'));
      now.addEventListener('click', () => {
        now.disabled = true;
        const done = () => {
          note.remove();
          actions.remove();
          if (panel) panel.remove();
          say('Thanks - we will ship this as soon as it is ready.');
        };
        if (shipChoice.demo) {
          done();
          return;
        }
        fetch(shipChoice.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ custom_order_id: request.id, action: 'ship_now' }),
        })
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            done();
          })
          .catch(() => {
            now.disabled = false;
            say('We could not send that just now. Please try again, or chat with us.');
          });
      });
      actions.appendChild(now);
    }

    if (canShipLater) {
      const panelId = `HarumShipLater-${String(request.id || request.reference || '').replace(/[^\w-]/g, '')}`;

      const later = element('button', 'btn btn--secondary btn--small');
      later.type = 'button';
      later.setAttribute('aria-expanded', 'false');
      later.setAttribute('aria-controls', panelId);
      later.appendChild(element('span', 'btn__text', 'Ship Later'));
      actions.appendChild(later);

      /* A real form post, like the wishlist's "Add to cart": it works with
         every cart type this theme can be configured with. */
      panel = element('form', 'harum-dash__ship-later');
      panel.id = panelId;
      panel.action = shipChoice.cartAddUrl;
      panel.method = 'post';
      panel.hidden = true;

      const fieldset = element('fieldset', 'harum-dash__ship-options');
      fieldset.appendChild(element('legend', 'harum-dash__ship-legend', 'How much longer should we hold it?'));
      shipChoice.options.forEach((option, index) => {
        const label = element('label', 'harum-dash__ship-option');
        const radio = document.createElement('input');
        radio.type = 'radio';
        radio.name = 'id';
        radio.value = option.id;
        radio.required = true;
        if (index === 0) radio.checked = true;
        label.appendChild(radio);
        label.appendChild(element('span', 'harum-dash__ship-option-title', option.title));
        // `money` = the HAR-221 currency converter's hook, as on the wishlist price.
        if (option.price) label.appendChild(element('span', 'harum-dash__ship-option-price money', option.price));
        fieldset.appendChild(label);
      });
      panel.appendChild(fieldset);

      const hidden = {
        quantity: '1',
        'properties[Custom order]': request.reference || request.name || String(request.id || ''),
        'properties[_custom_order_id]': String(request.id || ''),
      };
      Object.keys(hidden).forEach((name) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = hidden[name];
        panel.appendChild(input);
      });

      const submit = element('button', 'btn btn--primary btn--small');
      submit.type = 'submit';
      submit.appendChild(element('span', 'btn__text', 'Add to cart'));
      panel.appendChild(submit);

      if (shipChoice.demo) {
        panel.addEventListener('submit', (event) => {
          event.preventDefault();
          say('Sample only - nothing was added to your cart.');
        });
      }

      later.addEventListener('click', () => {
        const open = panel.hidden;
        panel.hidden = !open;
        later.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    wrap.appendChild(actions);
    if (panel) wrap.appendChild(panel);
    wrap.appendChild(message);
    return wrap;
  }

  function renderCustomOrderCard(request, shipChoice) {
    const status = normaliseStatus(request.status);
    const items = Array.isArray(request.items) ? request.items : [];

    const card = element('li', 'harum-dash__card');
    card.dataset.group = status.group;

    const top = element('div', 'harum-dash__card-top');
    const heading = document.createElement('div');
    heading.appendChild(
      element('p', 'harum-dash__card-ref', request.reference || request.name || `Request ${request.id || ''}`.trim())
    );

    const metaParts = [];
    if (request.created_at) metaParts.push(formatDate(request.created_at));
    if (items.length) metaParts.push(`${items.length} item${items.length > 1 ? 's' : ''}`);
    if (metaParts.length) heading.appendChild(element('p', 'harum-dash__card-meta', metaParts.join(' · ')));
    top.appendChild(heading);

    const badges = element('div', 'harum-dash__card-badges');
    badges.appendChild(element('span', `harum-dash__badge harum-dash__badge--${status.tint}`, status.label));
    top.appendChild(badges);
    card.appendChild(top);

    if (items.length) {
      const list = element('ul', 'harum-dash__items');
      items.forEach((item) => {
        const row = element('li', 'harum-dash__item');
        if (item.url) {
          const link = element('a', 'harum-dash__item-title', item.title || item.url);
          link.href = item.url;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          row.appendChild(link);
        } else {
          row.appendChild(element('span', 'harum-dash__item-title', item.title || 'Item'));
        }
        if (item.quantity) row.appendChild(element('span', 'harum-dash__item-qty', `Qty ${item.quantity}`));
        const price = formatAmount(item.price, request.currency);
        if (price) row.appendChild(element('span', 'harum-dash__item-price', price));
        list.appendChild(row);
      });
      card.appendChild(list);
    }

    if (request.tracking_number) {
      const tracking = element('div', 'harum-dash__tracking');
      tracking.appendChild(element('span', '', request.tracking_company || 'Tracking'));
      tracking.appendChild(element('span', 'harum-dash__tracking-number', request.tracking_number));
      if (request.tracking_url) {
        const link = element('a', 'harum-dash__link-button', 'Track parcel');
        link.href = request.tracking_url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        tracking.appendChild(link);
      }
      card.appendChild(tracking);
    }

    if (shipChoice && shipChoice.enabled && status.holdable) {
      const choice = renderShipChoice(request, shipChoice);
      if (choice) card.appendChild(choice);
    }

    const total = formatAmount(request.total, request.currency);
    if (total || request.invoice_url) {
      const body = element('div', 'harum-dash__card-body');
      if (!request.invoice_url) body.classList.add('harum-dash__card-body--end');

      /* An unpaid request carries the Shopify draft-order invoice CS emailed.
         Surfacing it here saves the customer digging through their inbox. */
      if (request.invoice_url) {
        const pay = element('a', 'btn btn--primary btn--small');
        pay.href = request.invoice_url;
        pay.appendChild(element('span', 'btn__text', 'Pay now'));
        body.appendChild(pay);
      }

      const side = element('div', 'harum-dash__card-side');
      if (total) side.appendChild(element('span', 'harum-dash__card-total', total));
      body.appendChild(side);
      card.appendChild(body);
    }

    return card;
  }

  function setupCustomOrders() {
    const container = document.querySelector('[data-harum-custom-orders]');
    if (!container) return;

    const list = container.querySelector('[data-harum-custom-orders-list]');
    const tabs = Array.from(container.querySelectorAll('[data-harum-custom-tab]'));
    const statTile = document.querySelector('[data-harum-stat="custom-orders"]');
    const navTotal = document.querySelector('[data-harum-custom-total]');
    const requestUrl = container.dataset.requestUrl || '/pages/buy-from-korea';
    const endpoint = (container.dataset.endpoint || '').trim();
    const demo =
      container.dataset.demo === 'true' || new URLSearchParams(window.location.search).has('harum_custom_orders_demo');

    /* HAR-12. Off until the section setting is ticked; demo mode always shows
       it, with sample holding options if no product has been picked yet. */
    let shipLaterOptions = [];
    const optionsScript = container.querySelector('[data-harum-ship-later-options]');
    if (optionsScript) {
      try {
        const parsed = JSON.parse(optionsScript.textContent);
        if (Array.isArray(parsed)) shipLaterOptions = parsed;
      } catch (error) {
        shipLaterOptions = [];
      }
    }
    if (demo && !shipLaterOptions.length) shipLaterOptions = SHIP_CHOICE_DEMO_OPTIONS;
    const shipChoice = {
      enabled: container.dataset.shipChoice === 'true' || demo,
      demo,
      endpoint: (container.dataset.shipNowEndpoint || '').trim(),
      cartAddUrl: container.dataset.cartAddUrl || '/cart/add',
      options: shipLaterOptions,
    };

    let requests = [];
    let activeTab = 'all';

    function emptyState(title, text, withButton) {
      const wrap = element('div', 'harum-dash__empty');
      wrap.appendChild(element('p', 'harum-dash__empty-title', title));
      wrap.appendChild(element('p', 'harum-dash__empty-text', text));
      if (withButton) {
        const link = element('a', 'btn btn--primary');
        link.href = requestUrl;
        link.appendChild(element('span', 'btn__text', 'Request Now'));
        wrap.appendChild(link);
      }
      return wrap;
    }

    function visibleRequests() {
      if (activeTab === 'all') return requests;
      return requests.filter((request) => normaliseStatus(request.status).group === activeTab);
    }

    function paintCounts() {
      const groups = requests.reduce((acc, request) => {
        const group = normaliseStatus(request.status).group;
        acc[group] = (acc[group] || 0) + 1;
        return acc;
      }, {});

      tabs.forEach((tab) => {
        const name = tab.dataset.harumCustomTab;
        const count = name === 'all' ? requests.length : groups[name] || 0;
        const badge = tab.querySelector('[data-harum-custom-count]');
        if (!badge) return;
        badge.textContent = count;
        badge.hidden = count === 0;
      });

      if (statTile) statTile.textContent = requests.length;
      if (navTotal) {
        navTotal.textContent = requests.length;
        navTotal.hidden = requests.length === 0;
      }
    }

    function paintList() {
      list.textContent = '';
      const rows = visibleRequests();

      if (!rows.length) {
        list.appendChild(
          requests.length
            ? emptyState('Nothing in this tab', 'None of your requests are at this stage right now.', false)
            : emptyState(
                'No custom order requests yet',
                'Send us a link to anything on sale in Korea and our team will buy it for you.',
                true
              )
        );
        return;
      }

      const cards = element('ul', 'harum-dash__list');
      rows.forEach((request) => cards.appendChild(renderCustomOrderCard(request, shipChoice)));
      list.appendChild(cards);
    }

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        activeTab = tab.dataset.harumCustomTab;
        tabs.forEach((other) => {
          other.setAttribute('aria-selected', other === tab ? 'true' : 'false');
        });
        paintList();
      });
    });

    if (demo) {
      requests = demoRequests();
      paintCounts();
      paintList();
      return;
    }

    if (!endpoint) {
      /* The Liquid-rendered "coming soon" state is already on the page. */
      return;
    }

    let url;
    try {
      url = new URL(endpoint, window.location.origin);
    } catch (error) {
      list.textContent = '';
      list.appendChild(
        emptyState('We could not load your requests', 'Please try again later or contact our team.', true)
      );
      return;
    }
    /* Nothing identifying is added here on purpose. The endpoint is a Shopify
       App Proxy path, so Shopify appends logged_in_customer_id and signs the
       query string with the app secret - that signature is what the backend
       trusts. Sending an email or customer id from the browser would prove
       nothing and would only invite someone to swap it for another. */
    fetch(url.toString(), { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        if (Array.isArray(payload)) requests = payload;
        else if (Array.isArray(payload.requests)) requests = payload.requests;
        else if (Array.isArray(payload.custom_orders)) requests = payload.custom_orders;
        else if (Array.isArray(payload.data)) requests = payload.data;
        else requests = [];
        paintCounts();
        paintList();
      })
      .catch(() => {
        list.textContent = '';
        list.appendChild(
          emptyState(
            'We could not load your requests',
            'Something went wrong on our side. Your requests are safe - please try again later.',
            true
          )
        );
      });
  }

  /* ------------------------------------------------------------------ *
   * 3. Wishlist
   * ------------------------------------------------------------------ */

  function paintWishlist() {
    const container = document.querySelector('[data-harum-wishlist-root]');
    if (!container || !window.HarumWishlist) return;

    const countBadge = document.querySelector('[data-harum-wishlist-count]');
    const clearButton = document.querySelector('[data-harum-wishlist-clear]');
    const browseUrl = container.dataset.browseUrl || '/collections/all';
    const addToCartUrl = container.dataset.addToCartUrl || '/cart/add';

    function card(item) {
      const wrap = element('li', 'harum-dash__wish');

      const media = element('a', 'harum-dash__wish-media');
      media.href = item.url || '#';
      if (item.image) {
        const img = document.createElement('img');
        img.src = item.image;
        img.alt = item.title || '';
        img.loading = 'lazy';
        img.width = 300;
        img.height = 300;
        media.appendChild(img);
      } else {
        /* The theme hides `a:empty` outright, which would collapse the square
           and leave the card ragged. A placeholder child keeps the grey tile. */
        media.appendChild(element('span', 'harum-dash__wish-placeholder'));
      }
      wrap.appendChild(media);

      const info = element('div', 'harum-dash__wish-info');
      const title = element('h3', 'harum-dash__wish-title');
      const titleLink = element('a', '', item.title || 'Product');
      titleLink.href = item.url || '#';
      title.appendChild(titleLink);
      info.appendChild(title);
      // `money` = the HAR-221 currency converter's hook; the saved price is the store-currency string.
      if (item.price) info.appendChild(element('span', 'harum-dash__wish-price money', item.price));

      const actions = element('div', 'harum-dash__wish-actions');
      if (item.variant_id) {
        /* A real form post rather than a fetch: it works with every cart type
           this theme can be configured with, drawer or page. */
        const form = document.createElement('form');
        form.action = addToCartUrl;
        form.method = 'post';
        form.style.flex = '1 1 auto';
        const id = document.createElement('input');
        id.type = 'hidden';
        id.name = 'id';
        id.value = item.variant_id;
        form.appendChild(id);
        const submit = element('button', 'btn btn--primary btn--small');
        submit.type = 'submit';
        submit.appendChild(element('span', 'btn__text', 'Add to cart'));
        form.appendChild(submit);
        actions.appendChild(form);
      } else {
        const view = element('a', 'btn btn--primary btn--small');
        view.href = item.url || browseUrl;
        view.appendChild(element('span', 'btn__text', 'View product'));
        actions.appendChild(view);
      }

      const remove = element('button', 'btn btn--secondary btn--small');
      remove.type = 'button';
      remove.dataset.harumWishlistRemove = item.id;
      remove.appendChild(element('span', 'btn__text', 'Remove'));
      actions.appendChild(remove);

      info.appendChild(actions);
      wrap.appendChild(info);
      return wrap;
    }

    function paint() {
      const items = window.HarumWishlist.items();

      if (countBadge) {
        countBadge.textContent = items.length;
        countBadge.hidden = items.length === 0;
      }
      if (clearButton) clearButton.hidden = items.length === 0;

      container.textContent = '';

      if (!items.length) {
        const empty = element('div', 'harum-dash__empty');
        empty.appendChild(element('p', 'harum-dash__empty-title', 'Your wishlist is empty'));
        empty.appendChild(
          element(
            'p',
            'harum-dash__empty-text',
            'Tap the heart on any product page to save it here. Saved items stay on this device.'
          )
        );
        const link = element('a', 'btn btn--primary');
        link.href = browseUrl;
        link.appendChild(element('span', 'btn__text', 'Browse products'));
        empty.appendChild(link);
        container.appendChild(empty);
        return;
      }

      const grid = element('ul', 'harum-dash__wishlist');
      items.forEach((item) => grid.appendChild(card(item)));
      container.appendChild(grid);
    }

    paint();
  }

  /* Delegated from the document, so it survives the section being replaced and
     is only ever bound once. */
  function onWishlistClick(event) {
    const remove = event.target.closest('[data-harum-wishlist-remove]');
    if (remove) {
      window.HarumWishlist.remove(remove.dataset.harumWishlistRemove);
      return;
    }
    const clear = event.target.closest('[data-harum-wishlist-clear]');
    if (clear && window.confirm('Remove every saved item?')) window.HarumWishlist.clear();
  }

  /* ------------------------------------------------------------------ *
   * 4. Addresses
   * ------------------------------------------------------------------ */

  function setupAddresses() {
    const container = document.querySelector('[data-customer-addresses]');
    if (!container) return;

    function message(form, text, kind) {
      let node = form.querySelector('.harum-dash__form-message');
      if (!node) {
        node = element('p', 'harum-dash__form-message');
        form.appendChild(node);
      }
      node.className = `harum-dash__form-message harum-dash__form-message--${kind}`;
      node.textContent = text;
    }

    /* Shopify answers an address POST with a redirect to /account/addresses.
       Posting it in the background and reloading keeps the customer here; the
       reload is what makes the new address appear, since the list is Liquid. */
    function post(action, body, form) {
      return fetch(action, {
        method: 'POST',
        body,
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      }).then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        window.location.hash = '#settings';
        window.location.reload();
      });
    }

    container.querySelectorAll('.harum-dash__address-form form').forEach((form) => {
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const submit = form.querySelector('button[type="submit"]');
        if (submit) submit.setAttribute('aria-busy', 'true');

        post(form.action, new FormData(form), form).catch(() => {
          if (submit) submit.removeAttribute('aria-busy');
          message(form, 'We could not save that address. Please check the fields and try again.', 'error');
        });
      });
    });

    container.addEventListener('click', (event) => {
      const cancel = event.target.closest('[data-harum-address-cancel]');
      if (cancel) {
        const target = document.getElementById(cancel.dataset.harumAddressCancel);
        if (target) target.classList.add('hidden');
        return;
      }

      const remove = event.target.closest('[data-harum-address-delete]');
      if (!remove) return;
      if (!window.confirm(remove.dataset.harumConfirm || 'Delete this address?')) return;

      const body = new FormData();
      body.append('_method', 'delete');
      post(remove.dataset.harumAddressDelete, body).catch(() => {
        window.alert('We could not delete that address. Please try again.');
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * 5. Chat With Us
   * ------------------------------------------------------------------ */

  /* Shopify Inbox injects its own button; there is no documented API to open
     the chat, so the reliable move is to click the widget's own control. When
     Inbox is not on the page (or has not loaded yet) we fall back to the link. */
  function setupChat() {
    const button = document.querySelector('[data-harum-chat]');
    if (!button) return;

    const selectors = [
      '#ShopifyChat',
      'shopify-chat',
      '[id^="ShopifyChat"]',
      '#chat-button-container button',
      '.shopify-chat-widget button',
    ];

    button.addEventListener('click', () => {
      for (let i = 0; i < selectors.length; i += 1) {
        const widget = document.querySelector(selectors[i]);
        if (!widget) continue;
        const clickable = widget.shadowRoot
          ? widget.shadowRoot.querySelector('button, [role="button"]')
          : widget.querySelector('button, [role="button"]') || widget;
        if (clickable && typeof clickable.click === 'function') {
          clickable.click();
          return;
        }
      }

      const fallback = button.dataset.fallbackUrl;
      if (fallback) window.location.href = fallback;
    });
  }

  function init() {
    const root = document.querySelector('[data-harum-dash]');
    if (!root || root.dataset.harumDashReady === 'true') return;
    root.dataset.harumDashReady = 'true';

    setupPanels(root);
    setupCustomOrders();
    paintWishlist();
    setupAddresses();
    setupChat();
  }

  document.addEventListener('click', (event) => {
    if (window.HarumWishlist) onWishlistClick(event);
  });
  /* Event name is HarumWishlist.event in assets/harum-wishlist.js - spelled out
     here so a missing wishlist script cannot take the whole dashboard down. */
  document.addEventListener('harum:wishlist:change', paintWishlist);

  /* The theme editor swaps the whole section element in and out, which drops
     every listener bound inside it - without this the dashboard is inert as soon
     as a merchant touches one of its settings. */
  document.addEventListener('shopify:section:load', init);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
